import Vue from 'vue';
import VueRouter, { Route, NavigationGuardNext } from 'vue-router';
import VueMeta from 'vue-meta';

import SettingsPages from '@/features/settings/routes';
import VolunteerSignupPages from '@/features/volunteer-signup/routes';
import AnalyticsPages from '@/features/analytics/routes';
import UnitTestsViewPages from '@/features/unit-tests-view/routes';
import UserPages from '@/features/user/routes';
import store from '@/common/store';

const PageNotFound = (): Promise<unknown> =>
  import('@/common/pages/NotFound.vue');

import { prefStorage } from '@samknows/utils';
import { setGlobalEventProperties } from '@/common/helpers/tracking-utils';
import { Accessibles } from '@/common/services/account/types';
import { getDefaultPath } from '@/common/services/account/userNavigation';
import { isUserSessionSetup } from '@/common/services/account/userSession';

Vue.use(VueRouter);
Vue.use(VueMeta);

const routes = [
  ...VolunteerSignupPages,
  ...SettingsPages,
  ...AnalyticsPages,
  ...UnitTestsViewPages,
  ...UserPages,
  {
    path: '/',
    redirect: '/volunteers',
    meta: {
      public: true
    }
  },
  {
    path: '/volunteers',
    redirect: `/volunteers/validate-session`,
    meta: {
      public: true
    }
  },
  {
    path: '*',
    component: PageNotFound,
    beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
      next();
    },
    meta: {
      public: true
    }
  }
];

const router = new VueRouter({
  mode: 'history',
  routes: routes,
  scrollBehavior(to) {
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  // Track changes in global event properties on each route change
  setGlobalEventProperties();

  if (to.meta.public) {
    next();
    return;
  }

  if (!isUserSessionSetup()) {
    next('/volunteers/validate-session');
    return;
  }

  // Check user has accepted the latest terms of services before allowing access to the app
  if (to.path !== '/volunteers/terms-of-services') {
    // Only do the check if the user is not already on the TOS page to prevent duplicated calls
    const showTOS = await store.dispatch('user/checkShowTermsOfServices');
    if (showTOS) {
      next(`/volunteers/terms-of-services`);
    }
  }

  // On routes with a guard
  if (to.meta.guard) {
    const accessibles: Accessibles = prefStorage.get('accessibles');

    if (!to.meta.guard(accessibles)) {
      const defaultPath = getDefaultPath();
      next(defaultPath);
      return;
    }
  }

  next();
});

export default router;
